import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSearch,
  faSortAmountDown,
  faSortAmountUp,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { CostData } from '../types';

interface ServiceBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: CostData | null;
}

interface ServiceItem {
  serviceName: string;
  totalCost: number;
  percentage: number;
  provider: 'aws' | 'gcp' | 'github';
  accountName?: string;
}

export const ServiceBreakdownModal: React.FC<ServiceBreakdownModalProps> = ({ isOpen, onClose, data }) => {
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(false);

  if (!isOpen || !data) return null;

  // Collect all services from all providers/accounts
  const allServices: ServiceItem[] = [];

  // AWS services from all accounts
  data.clouds.aws.accounts.forEach(account => {
    account.services.forEach(svc => {
      allServices.push({
        serviceName: svc.serviceName,
        totalCost: svc.totalCost,
        percentage: svc.percentage,
        provider: 'aws',
        accountName: account.accountName || account.accountId
      });
    });
  });

  // GCP services from all projects
  data.clouds.gcp.projects.forEach(project => {
    project.services.forEach(svc => {
      allServices.push({
        serviceName: svc.serviceName,
        totalCost: svc.totalCost,
        percentage: svc.percentage,
        provider: 'gcp',
        accountName: project.projectName || project.projectId
      });
    });
  });

  // GitHub usage
  data.clouds.github.orgs.forEach(org => {
    org.usage.forEach(u => {
      allServices.push({
        serviceName: `GitHub ${u.type.charAt(0).toUpperCase() + u.type.slice(1)}`,
        totalCost: u.cost,
        percentage: 0,
        provider: 'github',
        accountName: org.orgName
      });
    });
  });

  // Filter by search
  const filtered = allServices.filter(s =>
    s.serviceName.toLowerCase().includes(search.toLowerCase()) ||
    (s.accountName || '').toLowerCase().includes(search.toLowerCase())
  );

  // Sort by cost
  const sorted = [...filtered].sort((a, b) =>
    sortAsc ? a.totalCost - b.totalCost : b.totalCost - a.totalCost
  );

  const totalCost = allServices.reduce((sum, s) => sum + s.totalCost, 0);

  const getProviderIcon = (provider: string) => {
    if (provider === 'aws') return faAws;
    if (provider === 'gcp') return faGoogle;
    return faServer;
  };

  const getProviderColor = (provider: string) => {
    if (provider === 'aws') return 'text-yellow-600 bg-yellow-50';
    if (provider === 'gcp') return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  const formatCost = (cost: number) => {
    if (cost >= 1000) return `$${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (cost >= 1) return `$${cost.toFixed(2)}`;
    if (cost > 0) return `$${cost.toFixed(4)}`;
    return '$0.00';
  };

  const getCostBarWidth = (cost: number) => {
    const maxCost = sorted.length > 0 ? sorted[0].totalCost : 1;
    return Math.max(1, (cost / maxCost) * 100);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-mono" onClick={onClose}>
      <div className="bg-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-purple-600 px-4 py-3 flex items-center justify-between rounded-t">
          <div className="text-white">
            <h2 className="text-base font-bold">Cost Line Items</h2>
            <p className="text-xs text-purple-100 mt-0.5">
              {allServices.length} services across all providers — Total: {formatCost(totalCost)}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-purple-200 transition-colors">
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Search & Sort Bar */}
        <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search services..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-purple-400"
            />
          </div>
          <button
            onClick={() => setSortAsc(!sortAsc)}
            className="px-2 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={sortAsc ? faSortAmountUp : faSortAmountDown} />
            {sortAsc ? 'Low to High' : 'High to Low'}
          </button>
        </div>

        {/* Services List */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {sorted.length === 0 ? (
            <div className="p-8 text-center text-xs text-gray-500">
              No services found{search ? ` matching "${search}"` : ''}
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 px-4 py-2">Provider</th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-4 py-2">Service</th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-4 py-2">Account</th>
                  <th className="text-right text-xs font-semibold text-gray-600 px-4 py-2">Cost</th>
                  <th className="text-right text-xs font-semibold text-gray-600 px-4 py-2 w-32">Share</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((svc, idx) => {
                  const pctOfTotal = totalCost > 0 ? (svc.totalCost / totalCost) * 100 : 0;
                  return (
                    <tr key={`${svc.provider}-${svc.accountName}-${svc.serviceName}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${getProviderColor(svc.provider)}`}>
                          <FontAwesomeIcon icon={getProviderIcon(svc.provider)} />
                          {svc.provider.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs font-medium text-gray-900">{svc.serviceName}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{svc.accountName || '-'}</td>
                      <td className="px-4 py-2 text-xs font-bold text-gray-900 text-right">{formatCost(svc.totalCost)}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-purple-500 h-1.5 rounded-full"
                              style={{ width: `${Math.min(100, getCostBarWidth(svc.totalCost))}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 w-12 text-right">{pctOfTotal.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Showing {sorted.length} of {allServices.length} services
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
